import React from "react";

/**
 * Global error boundary. Prevents a single-panel render error from taking
 * out the whole Capexara Process Industry workspace. Shows a minimal, on-brand fallback and
 * offers the user a way to recover by resetting the boundary or reloading.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to browser console for users-facing debugging. We deliberately
    // do NOT swallow these — the error is still visible in the console.
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const message = error?.message || String(error);
    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          padding: 28,
          maxWidth: 620,
          margin: "60px auto",
          border: "1px solid var(--pf-border, #333)",
          background: "var(--pf-surface, #111)",
          color: "var(--pf-text, #eee)",
          borderRadius: 6,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: "0.14em",
            color: "var(--pf-err, #ff6b6b)",
            marginBottom: 10,
          }}
        >
          UI ERROR · PANEL RECOVERED
        </div>
        <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>
          Something went wrong rendering this view.
        </h2>
        <div
          style={{
            fontSize: 12.5,
            color: "var(--pf-text-muted, #bbb)",
            lineHeight: 1.55,
            marginBottom: 14,
          }}
        >
          Your data is safe — this is a display-side issue. You can try
          rendering the panel again, or reload the app.
        </div>
        <pre
          data-testid="error-boundary-message"
          style={{
            fontSize: 11,
            background: "var(--pf-surface-2, #0b0b0b)",
            padding: 10,
            border: "1px solid var(--pf-border, #333)",
            borderRadius: 4,
            color: "var(--pf-err, #ff6b6b)",
            maxHeight: 140,
            overflow: "auto",
            whiteSpace: "pre-wrap",
          }}
        >
          {message}
        </pre>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={this.reset}
            data-testid="error-boundary-retry"
          >
            Try again
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => window.location.reload()}
            data-testid="error-boundary-reload"
          >
            Reload app
          </button>
        </div>
      </div>
    );
  }
}
